// Per spec/blocks.md §button (P-037.28 / D-136). One of 4 atomic Blocks
// that replace the prior `commentary` + `text-block` Blocks (dropped at
// the v9 → v10 migration). Renders a freeform CTA button.
//
// **Background color via `ctx.ctaBgColor`** — D-136 introduces the
// `STYLE_CTA_BG` map + `CTA_BG_DEFAULT` constants (header.ts) +
// `RenderContext.ctaBgColor` field (types.ts) derived per-Email by
// `renderEmail`'s `deriveHeaderStyleContext` walk. Same first-included-
// Header dispatch as `linkColor` (D-118), `subheaderImageUrl` (D-125),
// `tintColor` (D-131) — fourth use of the unified-walk pattern. Default
// `#162443` navy when no Header block is present (matches the pre-D-136
// text-block button bg).
//
// **Outer cell padding: 30 px L/R + 0 top + 0 bottom.** Same content-
// gutter convention as `headline` + `text` + `author`. The button
// primitive emits a `<table>` (bulletproof for Outlook).
//
// **P-037.30 eighth post-Phase-Close §A — center-aligned.** Banks's
// audit reversed the D-136 planning decision that locked all 4 atomic
// Blocks to LEFT-aligned: only the `button` Block flips to center
// (headline / text / author stay left). Three coordinated changes:
// (1) the button primitive call passes `align: 'center'` so the
// primitive's outer `<table>` emits `align="center"` HTML attr +
// `margin: 0 auto;` inline style (belt-and-suspenders centering
// matching the D-134 `document-shell.ts` pattern). (2) the outer
// `<td>` here also carries `align="center"` so Outlook's Word engine
// reads it on the parent cell as a redundant defense-in-depth signal
// (some Outlook versions consult parent-cell `align` rather than
// child-table `align`). (3) the shrink-wrapped button table centers
// horizontally within the 30 px L/R padded gutter cell.
//
// **Auto-hide when either field is empty.** `ctaLabel` + `ctaUrl` are
// both required at the Zod layer (`min(1)` + `.url()` respectively);
// defense-in-depth: empty strings short-circuit to `EMPTY_HTML` rather
// than rendering an empty button.
//
// **Export name `buttonBlock` rather than `button`** to avoid a collision
// with the `button()` primitive (`src/primitives/button.ts`). The Block
// ID `'button'` stays canonical at the registry layer.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { button } from '../primitives/button'

const optionsSchema = z.object({})

const inputsSchema = z.object({
  ctaLabel: z.string().min(1),
  ctaUrl: z.string().url(),
})

export const buttonBlock = defineBlock({
  id: 'button',
  label: 'Button',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: { ctaLabel: 'Learn More', ctaUrl: 'https://example.com' },
  autoHide: ({ inputs }) => inputs.ctaLabel.length === 0 || inputs.ctaUrl.length === 0,
  render: ({ inputs }, ctx) => {
    const buttonHtml = button({
      text: inputs.ctaLabel,
      href: inputs.ctaUrl,
      bgcolor: ctx.ctaBgColor,
      align: 'center',
    })
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td align="center" style="padding: 0 30px;">${buttonHtml}</td></tr></table>`
  },
})
